import type { CSSProperties, ReactNode } from "react";

/**
 * Jedna tabulka pro celou aplikaci (artboard p7b).
 *
 * Devět obrazovek si dosud kreslilo vlastní mřížku vlastními šířkami, takže
 * se stejná data v auditu, u workerů a v plánu zarovnávala jinak. Tady je
 * jeden vzor: CSS grid, řádek `display: contents` (buňky jsou přímé položky
 * mřížky, takže se sloupce zarovnají napříč řádky) a **pod 768 px se řádek
 * složí do karty** — na mobilu se vodorovně neroluje.
 *
 * Buňka si nese popisek sloupce v `data-label`; složená karta ho vypíše před
 * hodnotu, jinak by z ní na mobilu zbyla nepopsaná řádka čísel.
 */

export type Column<Row> = {
  key: string;
  label: string;
  /** Šířka sloupce v desktopové mřížce; výchozí `minmax(0, 1fr)`. */
  width?: string;
  align?: "left" | "right";
  /** Sloupec, který ve složené kartě tvoří nadpis (popisek se u něj nevypisuje). */
  primary?: boolean;
  cell: (row: Row) => ReactNode;
};

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  testId,
  rowTestId,
  /**
   * Šířka, pod kterou se řádky skládají do karet. Výchozích 768 px stačí
   * na tři čtyři sloupce; tabulka o šesti se na tabletu slisuje, a tak se
   * skládá už pod 1024.
   */
  stackBelow = 768,
}: {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  empty?: ReactNode;
  testId?: string;
  rowTestId?: (row: Row) => string;
  stackBelow?: 768 | 1024;
}) {
  /**
   * Každý sloupec má podlahu. Bez ní se `minmax(0, …)` v úzkém kontejneru
   * smrskne na nulu a text se rozsype po písmenech pod sousední sloupec —
   * mřížka totiž nevidí šířku panelu, jen šířku okna.
   */
  const template = columns.map((c) => c.width ?? "minmax(120px, 1fr)").join(" ");

  if (rows.length === 0 && empty) {
    return <div data-testid={testId}>{empty}</div>;
  }

  return (
    <div className="af-table-scroll">
    <div
      className={stackBelow === 1024 ? "af-table af-table-wide" : "af-table"}
      style={{ "--af-table-cols": template } as CSSProperties}
      role="table"
      data-testid={testId}
    >
      <div className="af-table-head" role="row">
        {columns.map((c) => (
          <div
            key={c.key}
            role="columnheader"
            className="af-label-dim"
            style={{ textAlign: c.align ?? "left" }}
          >
            {c.label}
          </div>
        ))}
      </div>

      {rows.map((row) => (
        <div
          key={rowKey(row)}
          role="row"
          className={`af-table-row${onRowClick ? " af-table-row-click" : ""}`}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          onKeyDown={
            onRowClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(row);
                  }
                }
              : undefined
          }
          tabIndex={onRowClick ? 0 : undefined}
          data-testid={rowTestId?.(row)}
        >
          {columns.map((c) => (
            <div
              key={c.key}
              role="cell"
              data-label={c.primary ? undefined : c.label}
              className={c.primary ? "af-table-cell af-table-cell-primary" : "af-table-cell"}
              style={{ textAlign: c.align ?? "left" }}
            >
              {c.cell(row)}
            </div>
          ))}
        </div>
      ))}
    </div>
    </div>
  );
}
